import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Grid,
  Typography,
  FormHelperText
} from '@mui/material';
import PropTypes from 'prop-types';

const AddCategoriaForm = ({ 
  handleAddCategoria, 
  setSnackbar,
  categoriasExistentes = [],
  initialData = null
}) => {
  const [errors, setErrors] = useState({
    codigo: '',
    nombre: '',
    edadMin: '',
    edadMax: '',
    fechaCorte: ''
  });

  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    edadMin: '',
    edadMax: '',
    fechaCorte: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        codigo: initialData.codigo || '',
        nombre: initialData.nombre || '',
        edadMin: initialData.edadMin || '',
        edadMax: initialData.edadMax || '',
        fechaCorte: initialData.fechaCorte || ''
      });
    } else {
      const currentYear = new Date().getFullYear();
      setFormData(prev => ({
        ...prev,
        fechaCorte: `${currentYear}-12-31`
      }));
    }
  }, [initialData]);

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'codigo':
        if (!value.trim()) {
          error = 'El código es obligatorio';
        } else if (value.length > 6) {
          error = 'Máximo 6 caracteres';
        } else if (categoriasExistentes.some(cat => 
          cat.codigo === value.trim() && 
          (!initialData || cat.id !== initialData.id)
        )) {
          error = 'Este código ya existe';
        }
        break;
        
      case 'nombre':
        if (!value.trim()) {
          error = 'El nombre es obligatorio';
        } else if (value.length > 30) {
          error = 'Máximo 30 caracteres';
        } else if (categoriasExistentes.some(cat => 
          cat.nombre === value.trim() && 
          (!initialData || cat.id !== initialData.id)
        )) {
          error = 'Este nombre ya existe';
        }
        break;
        
      case 'edadMin':
        if (value === '') {
          error = 'La edad mínima es obligatoria';
        } else if (isNaN(value) || value < 0) {
          error = 'Debe ser un número positivo';
        } else if (formData.edadMax !== '' && Number(value) > Number(formData.edadMax)) {
          error = 'No puede ser mayor que la edad máxima';
        }
        break;
        
      case 'edadMax':
        if (value === '') {
          error = 'La edad máxima es obligatoria';
        } else if (isNaN(value) || value < 0) {
          error = 'Debe ser un número positivo';
        } else if (formData.edadMin !== '' && Number(value) < Number(formData.edadMin)) {
          error = 'No puede ser menor que la edad mínima';
        }
        break;
        
      case 'fechaCorte':
        if (!value) {
          error = 'La fecha de corte es obligatoria';
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          error = 'Formato inválido (YYYY-MM-DD)';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, value)
    }));
  };

  const validateForm = () => {
    const newErrors = {
      codigo: validateField('codigo', formData.codigo),
      nombre: validateField('nombre', formData.nombre),
      edadMin: validateField('edadMin', formData.edadMin),
      edadMax: validateField('edadMax', formData.edadMax),
      fechaCorte: validateField('fechaCorte', formData.fechaCorte)
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Por favor, corrige los errores en el formulario',
        severity: 'error'
      });
      return;
    }

    try {
      const categoriaData = {
        codigo: formData.codigo.trim(),
        nombre: formData.nombre.trim(),
        edadMin: Number(formData.edadMin),
        edadMax: Number(formData.edadMax),
        fechaCorte: formData.fechaCorte
      };

      const result = await handleAddCategoria(categoriaData);

      if (result && result.wasAdded) {
        // limpiar cuando es nuevo
        if (!initialData) {
          const currentYear = new Date().getFullYear();
          setFormData({
            codigo: '',
            nombre: '',
            edadMin: '',
            edadMax: '',
            fechaCorte: `${currentYear}-12-31`
          });
          setErrors({ codigo: '', nombre: '', edadMin: '', edadMax: '', fechaCorte: '' });
        }

        setSnackbar({
          open: true,
          message: initialData ? 'Categoría actualizada exitosamente' : 'Categoría añadida exitosamente',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result?.reason || 'No se pudo guardar la categoría. Error desconocido.',
          severity: 'error'
        });
      }

    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || 'Error al procesar la solicitud',
        severity: 'error'
      });
    }
  };

  return (
    <Box
      id="add-categoria-form"            // 👈 para que el botón del modal lo pueda disparar
      component="form"
      onSubmit={handleSubmit}
      sx={{
        mt: 2,
        '& .MuiFormHelperText-root': {
          position: 'absolute',
          bottom: '-22px'
        }
      }}
    >
      <Typography variant="h6" gutterBottom>
        {initialData ? 'Editar Categoría' : 'Nueva Categoría'}
      </Typography>
      
      <Grid container spacing={3} disableEqualOverflow>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Código *"
            name="codigo"
            value={formData.codigo}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={!!errors.codigo}
            helperText={errors.codigo}
            inputProps={{ maxLength: 6 }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nombre *"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={!!errors.nombre}
            helperText={errors.nombre}
            inputProps={{ maxLength: 30 }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            label="Edad Mínima *"
            name="edadMin"
            type="number"
            value={formData.edadMin}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={!!errors.edadMin}
            helperText={errors.edadMin}
            inputProps={{ min: 0 }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            label="Edad Máxima *"
            name="edadMax"
            type="number"
            value={formData.edadMax}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={!!errors.edadMax}
            helperText={errors.edadMax}
            inputProps={{ min: 0 }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <TextField
            label="Fecha de Corte *"
            name="fechaCorte"
            type="date"
            value={formData.fechaCorte}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={!!errors.fechaCorte}
            helperText={errors.fechaCorte}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {/* 👇 Sin botones internos: el modal aporta Cancelar / Añadir */}
      <Box mt={2}>
        <FormHelperText>* Campos obligatorios</FormHelperText>
      </Box>
    </Box>
  );
};

AddCategoriaForm.propTypes = {
  handleAddCategoria: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func.isRequired,
  categoriasExistentes: PropTypes.array,
  initialData: PropTypes.object
};

export default AddCategoriaForm;
